import React, { useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Sheet, FileDown, Upload, Loader2, CheckCircle2, AlertTriangle, ShieldAlert, XCircle,
} from "lucide-react";
import { downloadBlob } from "@/components/DataTable";

export default function MasterHistoricalImport() {
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  async function downloadTemplate() {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/import/master/template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Download failed"); return; }
    await downloadBlob(res, "HISPL_Master_Historical_Data_Template.xlsx");
  }

  function reset() {
    setFile(null); setPreview(null); setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function runPreview() {
    if (!file) { toast.error("Choose an Excel/CSV file first."); return; }
    setPreviewing(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/master/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally { setPreviewing(false); }
  }

  async function runCommit() {
    if (!file) { toast.error("Choose a file first."); return; }
    setCommitting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/master/commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.success) {
        const total = Object.values(data.report.created).reduce((a, b) => a + b, 0)
                    + Object.values(data.report.updated).reduce((a, b) => a + b, 0);
        toast.success(`Master import complete — ${total} records processed.`);
      } else {
        toast.error("Import blocked — see validation errors below.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setCommitting(false); }
  }

  async function downloadReport() {
    if (!result?.report) return;
    try {
      const res = await api.post("/import/master/report/download", result.report, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "Master_Import_Report.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Report download failed"); }
  }

  const hasBlockingErrors = preview && preview.errors && preview.errors.length > 0;

  return (
    <Card className="border-red-200 bg-gradient-to-br from-red-50/40 to-white" data-testid="master-import-card">
      <div className="px-5 py-4 border-b border-red-100 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-red-700 font-semibold flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5" />
            One-Shot Migration Tool
          </div>
          <h2 className="text-xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Sheet className="w-5 h-5 text-red-700" />
            Master Historical Data Import
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Upload one Excel (.xlsx) or CSV file exported from your Google Sheet — the ERP will
            create, update, and auto-link Inquiry → Quotation → PO → OA → Cylinder in a single pass.
            Partial rows (cylinder-only, cylinder + dispatch, etc.) are supported.
          </p>
        </div>
        <Button variant="outline" onClick={downloadTemplate} data-testid="master-template-btn">
          <FileDown className="w-4 h-4 mr-2" />
          Download Template
        </Button>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            data-testid="master-file-input"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
            className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-red-700 file:text-white file:px-4 file:py-2 file:font-medium hover:file:bg-red-800"
          />
          {file && (
            <div className="text-xs text-slate-600 truncate flex-1">
              <span className="font-mono">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={runPreview}
              disabled={!file || previewing}
              data-testid="master-preview-btn"
            >
              {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Validate
            </Button>
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={runCommit}
              disabled={!file || committing || hasBlockingErrors}
              data-testid="master-commit-btn"
            >
              {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Run Import
            </Button>
            {(preview || result) && (
              <Button variant="ghost" onClick={reset} data-testid="master-reset-btn">Reset</Button>
            )}
          </div>
        </div>

        {/* Validation preview */}
        {preview && (
          <div className="border rounded-lg p-3 bg-white" data-testid="master-preview-panel">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
              Validation ({preview.row_count} rows)
            </div>
            {preview.missing_columns?.length ? (
              <div className="text-sm text-red-700 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Missing required columns: <strong>{preview.missing_columns.join(", ")}</strong>
              </div>
            ) : hasBlockingErrors ? (
              <div className="space-y-1 text-sm text-red-700 max-h-40 overflow-auto">
                <div className="flex items-center gap-2 font-semibold">
                  <XCircle className="w-4 h-4" /> {preview.errors.length} blocking error(s):
                </div>
                <ul className="list-disc list-inside">
                  {preview.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>Row {e.row}: {e.type} {e.detail ? `— ${JSON.stringify(e.detail)}` : ""} {e.field ? `(${e.field})` : ""}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> {preview.row_count} row(s) look valid.
              </div>
            )}
            {preview.warnings?.length ? (
              <div className="mt-2 text-xs text-amber-700">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                {preview.warnings.length} warning(s) (non-blocking):
                <ul className="list-disc list-inside ml-4">
                  {preview.warnings.slice(0, 8).map((w, i) => (
                    <li key={i}>Row {w.row}: {w.type} — {w.field} = {w.value}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}

        {/* Import result */}
        {result?.report && (
          <div className="border rounded-lg p-4 bg-white" data-testid="master-result-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                Import Report
              </div>
              <Button size="sm" variant="outline" onClick={downloadReport} data-testid="master-report-download">
                <FileDown className="w-3.5 h-3.5 mr-1" />
                Download Report (Excel)
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <StatBox label="Rows processed" value={result.report.rows_processed} />
              <StatBox label="Links established" value={result.report.linked} />
              <StatBox label="Skipped" value={result.report.skipped}
                       accent={result.report.skipped > 0 ? "amber" : null} />
              <StatBox label="Errors" value={result.report.errors?.length || 0}
                       accent={result.report.errors?.length > 0 ? "red" : null} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Created</div>
                <ul className="text-xs space-y-0.5">
                  {Object.entries(result.report.created).map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span className="text-slate-700">{k.replace(/_/g, " ")}</span>
                      <span className="font-mono font-semibold text-emerald-700">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Updated</div>
                <ul className="text-xs space-y-0.5">
                  {Object.entries(result.report.updated).map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span className="text-slate-700">{k.replace(/_/g, " ")}</span>
                      <span className="font-mono font-semibold text-blue-700">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {result.report.errors?.length ? (
              <div className="mt-4 text-xs">
                <div className="text-red-700 font-semibold mb-1">Errors ({result.report.errors.length})</div>
                <ul className="text-red-700 list-disc list-inside max-h-32 overflow-auto">
                  {result.report.errors.slice(0, 15).map((e, i) => (
                    <li key={i}>Row {e.row}: {e.detail}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}

function StatBox({ label, value, accent }) {
  const accentCls = accent === "red" ? "text-red-700 bg-red-50 border-red-200"
                  : accent === "amber" ? "text-amber-700 bg-amber-50 border-amber-200"
                  : "text-slate-900 bg-slate-50 border-slate-200";
  return (
    <div className={`border rounded-lg p-3 ${accentCls}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-75">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value ?? 0}</div>
    </div>
  );
}
